import { Download, File, FileImage, FileText, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { fetchDownloadUrl } from '../../services/admin-api';
import type { FileAttachmentPart, SubmittedFileInfo } from './task-detail-types';
import { downloadFromUrl, formatFileSize } from './task-detail-utils';

/**
 * MIMEタイプに基づいてアイコンを取得
 */
function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) {
    return <FileImage className="w-4 h-4 text-green-500" />;
  }
  if (mimeType.startsWith('text/') || mimeType.includes('pdf')) {
    return <FileText className="w-4 h-4 text-blue-500" />;
  }
  return <File className="w-4 h-4 text-muted-foreground" />;
}

/**
 * ファイル添付表示コンポーネント
 */
export function FileAttachmentCard({ attachment }: { attachment: FileAttachmentPart }) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = useCallback(async () => {
    if (!attachment.s3Key) {
      setDownloadError('ダウンロード用のキーがありません');
      return;
    }

    setIsDownloading(true);
    setDownloadError(null);

    try {
      const { downloadUrl } = await fetchDownloadUrl(attachment.s3Key);
      await downloadFromUrl(downloadUrl, attachment.name);
    } catch (error) {
      console.error('Download failed:', error);
      setDownloadError('ダウンロードに失敗しました');
    } finally {
      setIsDownloading(false);
    }
  }, [attachment.s3Key, attachment.name]);

  return (
    <div className="flex items-center gap-2 p-2 rounded bg-muted text-sm">
      {getFileIcon(attachment.mimeType)}
      <span className="text-foreground flex-1 truncate">{attachment.name}</span>
      <span className="text-muted-foreground text-xs">({formatFileSize(attachment.size)})</span>
      {attachment.s3Key && (
        <button
          type="button"
          onClick={handleDownload}
          disabled={isDownloading}
          className="p-1 rounded hover:bg-accent transition-colors disabled:opacity-50"
          title="ダウンロード"
        >
          {isDownloading ? (
            <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />
          ) : (
            <Download className="w-4 h-4 text-muted-foreground" />
          )}
        </button>
      )}
      {downloadError && <span className="text-red-400 text-xs">{downloadError}</span>}
    </div>
  );
}

/**
 * 提出ファイル表示コンポーネント（desktop-appと同じカードスタイル）
 */
export function FileSubmissionCard({
  files,
  message,
}: {
  files: SubmittedFileInfo[];
  message: string | undefined;
}) {
  return (
    <div className="mt-2 space-y-2">
      {message && <p className="text-sm text-muted-foreground">{message}</p>}
      <div className="flex flex-wrap gap-3">
        {files.map((file) => (
          <FileSubmissionItem key={file.submissionId} file={file} />
        ))}
      </div>
    </div>
  );
}

/**
 * 提出ファイルアイテムコンポーネント（desktop-appと同じカードスタイル）
 */
function FileSubmissionItem({ file }: { file: SubmittedFileInfo }) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoadingImage, setIsLoadingImage] = useState(false);

  const isImage = file.mimeType.startsWith('image/');

  // 画像の場合はプレビューURLを取得
  useEffect(() => {
    if (isImage && file.s3Key) {
      setIsLoadingImage(true);
      fetchDownloadUrl(file.s3Key)
        .then(({ downloadUrl }) => setImageUrl(downloadUrl))
        .catch((error) => console.error('Failed to load image preview:', error))
        .finally(() => setIsLoadingImage(false));
    }
  }, [isImage, file.s3Key]);

  const handleDownload = useCallback(async () => {
    if (!file.s3Key) {
      setDownloadError('ダウンロード用のキーがありません');
      return;
    }

    setIsDownloading(true);
    setDownloadError(null);

    try {
      const { downloadUrl } = await fetchDownloadUrl(file.s3Key);
      await downloadFromUrl(downloadUrl, file.name);
    } catch (error) {
      console.error('Download failed:', error);
      setDownloadError('ダウンロードに失敗しました');
    } finally {
      setIsDownloading(false);
    }
  }, [file.s3Key, file.name]);

  return (
    <div className="group relative flex w-48 flex-col rounded-lg border border-border bg-muted p-2">
      {/* ファイルアイコン / 画像サムネイル */}
      <div className="mb-2">
        {isImage && imageUrl ? (
          <a
            href={imageUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="relative block h-32 w-full overflow-hidden rounded border border-border bg-card transition-opacity hover:opacity-80"
          >
            <img src={imageUrl} alt={file.name} className="h-full w-full object-cover" />
          </a>
        ) : isImage && isLoadingImage ? (
          <div className="flex h-32 w-full items-center justify-center rounded border border-border bg-card">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex h-16 w-full items-center justify-center rounded border border-border bg-card">
            {getFileIcon(file.mimeType)}
          </div>
        )}
      </div>

      {/* ファイル情報 */}
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-sm text-foreground" title={file.name}>
          {file.name}
        </p>
        <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
        {downloadError && <p className="mt-1 text-xs text-red-400">{downloadError}</p>}
      </div>

      {/* ダウンロードボタン */}
      {file.s3Key && (
        <button
          type="button"
          onClick={handleDownload}
          disabled={isDownloading}
          className="absolute right-2 bottom-2 p-1.5 rounded bg-accent opacity-0 transition-opacity group-hover:opacity-100 hover:bg-accent disabled:opacity-50"
          title="ダウンロード"
        >
          {isDownloading ? (
            <Loader2 className="w-4 h-4 text-foreground animate-spin" />
          ) : (
            <Download className="w-4 h-4 text-foreground" />
          )}
        </button>
      )}
    </div>
  );
}
